import {
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
// @sentry/angular is statically imported here because Angular's ErrorHandler
// provider must be registered at app-config time. environment.sentryDsn gates
// whether the provider is registered (and Sentry.init is called in main.ts);
// when unset, Sentry is loaded but never initialised.
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { authInterceptor } from './features/auth/interceptors/auth.interceptor';
import { refreshInterceptor } from './features/auth/interceptors/refresh.interceptor';
import { errorInterceptor } from './shared/api/error.interceptor';
import { environment } from '../environments/environment';

import { Configuration as AuthApiConfiguration } from '../apis/auth-api/client';
import { Configuration as ContainersApiConfiguration } from '../apis/containers-api/client';
import { Configuration as TerminalApiConfiguration } from '../apis/terminal-api/client';
import { Configuration as TrackedProductUnitsApiConfiguration } from '../apis/tracked-product-units-api/client';
import { Configuration as WarehouseLocationsApiConfiguration } from '../apis/warehouse-locations-api/client';

const apiConfigFactory = <T>(ctor: new (params: { basePath: string }) => T): (() => T) => {
  return () => new ctor({ basePath: environment.apiUrl });
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, refreshInterceptor, errorInterceptor])),
    { provide: AuthApiConfiguration, useFactory: apiConfigFactory(AuthApiConfiguration) },
    {
      provide: ContainersApiConfiguration,
      useFactory: apiConfigFactory(ContainersApiConfiguration),
    },
    { provide: TerminalApiConfiguration, useFactory: apiConfigFactory(TerminalApiConfiguration) },
    {
      provide: TrackedProductUnitsApiConfiguration,
      useFactory: apiConfigFactory(TrackedProductUnitsApiConfiguration),
    },
    {
      provide: WarehouseLocationsApiConfiguration,
      useFactory: apiConfigFactory(WarehouseLocationsApiConfiguration),
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    ...(environment.sentryDsn
      ? [{ provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) }]
      : []),
  ],
};
